import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useAuthenticator } from '@aws-amplify/ui-react';
import api from '../services/api';
import './Settings.css';

type SettingsTab = 'profile' | 'preferences' | 'privacy' | 'account';

interface UserPreferences {
  autoplay: boolean;
  defaultVolume: number;
  showListenHistory: boolean;
  emailNotifications: boolean;
  pushNotifications: boolean;
}

interface PrivacySettings {
  profileVisibility: 'public' | 'private';
  showListeningActivity: boolean;
  showFavorites: boolean;
}

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { signOut } = useAuthenticator();
  const [activeTab, setActiveTab] = useState<SettingsTab>('preferences');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [preferences, setPreferences] = useState<UserPreferences>({
    autoplay: true,
    defaultVolume: 80,
    showListenHistory: true,
    emailNotifications: true,
    pushNotifications: false
  });

  const [privacy, setPrivacy] = useState<PrivacySettings>({
    profileVisibility: 'public',
    showListeningActivity: true,
    showFavorites: true
  });

  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [prefsResponse, privacyResponse] = await Promise.all([
        api.get<UserPreferences>('/user/preferences').catch(() => null),
        api.get<PrivacySettings>('/user/privacy').catch(() => null)
      ]);

      if (prefsResponse) {
        setPreferences(prev => ({ ...prev, ...prefsResponse }));
      }
      if (privacyResponse) {
        setPrivacy(prev => ({ ...prev, ...privacyResponse }));
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load settings';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const savePreferences = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await api.put('/user/preferences', preferences);
      setSuccess('Preferences saved successfully');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save preferences';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const savePrivacy = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await api.put('/user/privacy', privacy);
      setSuccess('Privacy settings saved successfully');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save privacy settings';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }

    try {
      setSaving(true);
      await api.delete('/user/account');
      signOut();
      navigate('/');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete account';
      setError(message);
      setSaving(false);
    }
  };

  const renderProfileTab = () => (
    <div className="settings-section">
      <h2>Profile Settings</h2>
      <p className="section-description">
        Manage your profile information, avatar, and bio.
      </p>
      <button
        onClick={() => navigate('/profile')}
        className="btn btn-primary"
      >
        Edit Profile
      </button>
    </div>
  );

  const renderPreferencesTab = () => (
    <div className="settings-section">
      <h2>Playback Preferences</h2>

      <div className="setting-item">
        <div className="setting-info">
          <label>Autoplay</label>
          <p>Automatically play the next stream when current one ends</p>
        </div>
        <label className="toggle">
          <input
            type="checkbox"
            checked={preferences.autoplay}
            onChange={(e) => setPreferences(prev => ({ ...prev, autoplay: e.target.checked }))}
          />
          <span className="toggle-slider" />
        </label>
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>Default Volume</label>
          <p>Set the default volume level ({preferences.defaultVolume}%)</p>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          value={preferences.defaultVolume}
          onChange={(e) => setPreferences(prev => ({ ...prev, defaultVolume: parseInt(e.target.value) }))}
          className="volume-slider"
        />
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>Save Listen History</label>
          <p>Keep track of streams you've listened to</p>
        </div>
        <label className="toggle">
          <input
            type="checkbox"
            checked={preferences.showListenHistory}
            onChange={(e) => setPreferences(prev => ({ ...prev, showListenHistory: e.target.checked }))}
          />
          <span className="toggle-slider" />
        </label>
      </div>

      <h2>Notification Preferences</h2>

      <div className="setting-item">
        <div className="setting-info">
          <label>Email Notifications</label>
          <p>Receive email updates about new followers and activity</p>
        </div>
        <label className="toggle">
          <input
            type="checkbox"
            checked={preferences.emailNotifications}
            onChange={(e) => setPreferences(prev => ({ ...prev, emailNotifications: e.target.checked }))}
          />
          <span className="toggle-slider" />
        </label>
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>Push Notifications</label>
          <p>Receive browser push notifications</p>
        </div>
        <label className="toggle">
          <input
            type="checkbox"
            checked={preferences.pushNotifications}
            onChange={(e) => setPreferences(prev => ({ ...prev, pushNotifications: e.target.checked }))}
          />
          <span className="toggle-slider" />
        </label>
      </div>

      <div className="settings-actions">
        <button
          onClick={savePreferences}
          disabled={saving}
          className="btn btn-primary"
        >
          {saving ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );

  const renderPrivacyTab = () => (
    <div className="settings-section">
      <h2>Privacy Settings</h2>

      <div className="setting-item">
        <div className="setting-info">
          <label>Profile Visibility</label>
          <p>Control who can see your profile</p>
        </div>
        <select
          value={privacy.profileVisibility}
          onChange={(e) => setPrivacy(prev => ({ ...prev, profileVisibility: e.target.value as 'public' | 'private' }))}
          className="setting-select"
        >
          <option value="public">Public</option>
          <option value="private">Private</option>
        </select>
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>Show Listening Activity</label>
          <p>Let others see what you're listening to</p>
        </div>
        <label className="toggle">
          <input
            type="checkbox"
            checked={privacy.showListeningActivity}
            onChange={(e) => setPrivacy(prev => ({ ...prev, showListeningActivity: e.target.checked }))}
          />
          <span className="toggle-slider" />
        </label>
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>Show Favorites</label>
          <p>Let others see your favorite streams</p>
        </div>
        <label className="toggle">
          <input
            type="checkbox"
            checked={privacy.showFavorites}
            onChange={(e) => setPrivacy(prev => ({ ...prev, showFavorites: e.target.checked }))}
          />
          <span className="toggle-slider" />
        </label>
      </div>

      <div className="settings-actions">
        <button
          onClick={savePrivacy}
          disabled={saving}
          className="btn btn-primary"
        >
          {saving ? 'Saving...' : 'Save Privacy Settings'}
        </button>
      </div>
    </div>
  );

  const renderAccountTab = () => (
    <div className="settings-section">
      <h2>Account Settings</h2>

      <div className="account-info">
        <p>Manage your account settings and data.</p>
      </div>

      <div className="setting-item">
        <div className="setting-info">
          <label>Sign Out</label>
          <p>Sign out of your account on this device</p>
        </div>
        <button onClick={() => signOut()} className="btn btn-secondary">
          Sign Out
        </button>
      </div>

      <div className="danger-zone">
        <h3>Danger Zone</h3>
        <div className="setting-item danger">
          <div className="setting-info">
            <label>Delete Account</label>
            <p>Permanently delete your account and all data. This cannot be undone.</p>
          </div>
          <button
            onClick={handleDeleteAccount}
            disabled={saving}
            className="btn btn-danger"
          >
            {deleteConfirm ? 'Click again to confirm' : 'Delete Account'}
          </button>
        </div>
        {deleteConfirm && (
          <button
            onClick={() => setDeleteConfirm(false)}
            className="btn btn-secondary cancel-delete"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="settings-page">
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <header className="page-header">
        <h1>Settings</h1>
        <p className="subtitle">Manage your account preferences</p>
      </header>

      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError(null)} className="alert-close">×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
          <button onClick={() => setSuccess(null)} className="alert-close">×</button>
        </div>
      )}

      <div className="settings-layout">
        <nav className="settings-nav">
          <button
            className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            Profile
          </button>
          <button
            className={`nav-item ${activeTab === 'preferences' ? 'active' : ''}`}
            onClick={() => setActiveTab('preferences')}
          >
            Preferences
          </button>
          <button
            className={`nav-item ${activeTab === 'privacy' ? 'active' : ''}`}
            onClick={() => setActiveTab('privacy')}
          >
            Privacy
          </button>
          <button
            className={`nav-item ${activeTab === 'account' ? 'active' : ''}`}
            onClick={() => setActiveTab('account')}
          >
            Account
          </button>
        </nav>

        <div className="settings-content">
          {activeTab === 'profile' && renderProfileTab()}
          {activeTab === 'preferences' && renderPreferencesTab()}
          {activeTab === 'privacy' && renderPrivacyTab()}
          {activeTab === 'account' && renderAccountTab()}
        </div>
      </div>
    </div>
  );
};

export default Settings;
